// =============================================================================
// LIGA AJUPAM — Cuándo DESTACAR el Simulacro (v6.11.72 · v6.11.76)
// =============================================================================
// Ojo con lo que decide este hook: **no** decide si el simulacro está
// disponible, sino si se lo empuja. Desde v6.11.76 se puede repetir siempre —
// vive permanentemente en el Centro de Ayuda. Lo que se apaga al completarlo es
// la tarjeta destacada del Inicio, que es un empujón y no un menú.
//
// A diferencia de `useRulebookCtaWindow`, acá NO hay ventana de días. El CTA del
// reglamento dura lo que el admin diga porque es un recordatorio; el empujón al
// simulacro dura hasta que la persona lo hizo.
//
// Se pide, eso sí, que la app esté ENTERA habilitada: empujar un simulacro sobre
// cómo programar partidos mientras Partidos todavía está candadeada enseña un
// flujo que la persona no puede practicar al salir. Ese es el mismo criterio que
// usa `RulebookCta`, y para el que se inscribe tarde no arranca con la liga sino
// con su propia alta.
//
// -----------------------------------------------------------------------------
// Por qué `pending` arranca en false y no en true
// -----------------------------------------------------------------------------
// `getSimulacroCompletedVersion` devuelve `null` ante CUALQUIER problema, y
// `null` significa "nunca lo hizo". Con la RPC rota (era STABLE y fallaba en
// cada llamada desde el navegador, ver v6.11.76) eso se traducía en empujarle la
// tarjeta a gente que ya lo había hecho: "lo completé, desapareció, y volvió a
// aparecer". El estado `undefined` —todavía no sé— existe justamente para no
// afirmar "no lo hizo" mientras no haya respuesta.

import { useCallback, useEffect, useState } from 'react';
import { getSimulacroCompletedVersion } from '../services/api/simulacro';
import { SIMULACRO_VERSION } from '../data/simulacroScenes';

interface Params {
  sessionToken?: string | null;
}

interface Result {
  /**
   * true si esta persona todavía no completó el simulacro, o sea si corresponde
   * DESTACARLO. Nunca condiciona el acceso: el simulacro se puede repetir
   * siempre desde el Centro de Ayuda.
   *
   * Es un hecho del servidor y nada más: **no** incluye las condiciones de
   * pantalla (que la app esté entera habilitada, que haya equipo). Eso lo decide
   * quien renderiza.
   *
   * La separación no es cosmética. Este hook tiene que llamarse siempre, en
   * todos los renders de `App`, y `App` tiene early returns por ruta mucho antes
   * de saber si las secciones están candadeadas. Si el hook dependiera de eso,
   * habría que llamarlo tarde — y un hook después de un early return cambia de
   * orden entre renders, que es exactamente lo que React prohíbe.
   */
  pending: boolean;
  /** Marca local de completado, para que la tarjeta se apague sin esperar red. */
  markDone: () => void;
}

export function useSimulacroGate({ sessionToken }: Params): Result {
  // `undefined` = todavía no sabemos. Distinto de `null` (= nunca lo hizo): sin
  // esa diferencia, la carta parpadea en cada arranque para quien ya lo hizo.
  const [completedVersion, setCompletedVersion] = useState<number | null | undefined>(undefined);
  // Terminarlo en esta sesión es irreversible para la UI. Sin esto, cualquier
  // re-consulta posterior que falle (devuelve null = "nunca lo hizo") le vuelve
  // a poner la tarjeta a alguien que acaba de terminarlo delante nuestro.
  const [doneLocally, setDoneLocally] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Sin sesión no se consulta y tampoco se resetea el estado: `pending` ya
    // exige `sessionToken`, así que un valor viejo no puede mostrar la carta, y
    // un setState acá sería un render en cascada al pedo.
    if (!sessionToken) return;
    (async () => {
      const v = await getSimulacroCompletedVersion(sessionToken);
      if (!cancelled) setCompletedVersion(v);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  const markDone = useCallback(() => {
    setDoneLocally(true);
    setCompletedVersion(SIMULACRO_VERSION);
  }, []);

  const pending =
    !!sessionToken &&
    !doneLocally &&
    completedVersion !== undefined &&
    (completedVersion ?? 0) < SIMULACRO_VERSION;

  return { pending, markDone };
}
